import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { API, formatApiError } from "@/lib/api";

const STATUSES = ["new", "contacted", "won", "archived"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminLeads() {
  const [user, setUser] = useState(undefined);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const loadLeads = () => {
    setLoading(true);
    axios
      .get(`${API}/leads`, { withCredentials: true })
      .then((r) => setLeads(r.data))
      .catch((e) => toast.error(formatApiError(e, "Could not load leads.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) loadLeads();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(data);
      toast.success(`Welcome back, ${data.name}.`);
    } catch (err) {
      setError(formatApiError(err, "Login failed."));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setLeads([]);
  };

  const setStatus = async (leadId, status) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${leadId}`, { status }, { withCredentials: true });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? data : l)));
      toast.success(`Lead marked ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not update lead."));
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]" data-testid="admin-loading">
        <p className="font-mono text-sm text-slate-500">Checking session…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4" data-testid="admin-login-view">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
          <div className="flex items-center gap-3">
            <LogoMark className="h-11 w-11" />
            <div>
              <h1 className="font-display text-xl font-extrabold text-navy">Lead Desk</h1>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-600">Evolve Elite Admin</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-8 space-y-4">
            <div>
              <label className="ee-label" htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                type="email"
                className="ee-input"
                placeholder="admin@evolveelite.in"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                data-testid="admin-login-email-input"
              />
            </div>
            <div>
              <label className="ee-label" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                className="ee-input"
                placeholder="••••••••"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                data-testid="admin-login-password-input"
              />
            </div>
            {error && (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary w-full" data-testid="admin-login-submit-btn">
              <ShieldCheck className="h-4 w-4" /> Sign in
            </button>
          </form>
          <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm font-bold text-slate-500 transition hover:text-navy" data-testid="admin-back-to-site-link">
            <ArrowLeft className="h-4 w-4" /> Back to website
          </Link>
        </div>
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});
  const visible = statusFilter === "all" ? leads : leads.filter((l) => l.status === statusFilter);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20" data-testid="admin-leads-view">
      <header className="border-b border-slate-200 bg-white">
        <div className="container-x flex flex-wrap items-center justify-between gap-4 py-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <div>
              <h1 className="font-display text-xl font-extrabold text-navy">Lead Desk</h1>
              <p className="text-xs text-slate-500">{user.email} · CRM follow-up view</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-outline !px-5 !py-2.5" data-testid="admin-view-site-link">View Site</Link>
            <button onClick={logout} className="btn-primary !px-5 !py-2.5" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x pt-10">
        <div className="flex flex-wrap items-center gap-3" data-testid="admin-status-filter">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full border px-4 py-2 text-sm font-bold capitalize transition ${
                statusFilter === s ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-navy hover:border-navy/40"
              }`}
              data-testid={`admin-filter-${s}`}
            >
              {s} {s !== "all" && `(${counts[s] ?? 0})`}
            </button>
          ))}
          <button onClick={loadLeads} className="ml-auto rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-navy transition hover:border-navy/40" data-testid="admin-refresh-btn">
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="leads-table">
          {visible.length === 0 ? (
            <p className="px-6 py-16 text-center text-sm text-slate-500" data-testid="leads-empty-state">
              {loading ? "Loading leads…" : "No leads here yet. Every form submission on the website will appear in this desk."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-[#F8FAFC] font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-5 py-4">Received</th>
                    <th className="px-5 py-4">Name</th>
                    <th className="px-5 py-4">Phone</th>
                    <th className="px-5 py-4">Service</th>
                    <th className="px-5 py-4">Source</th>
                    <th className="px-5 py-4">Message</th>
                    <th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 align-top transition hover:bg-gold/5" data-testid={`lead-row-${lead.id}`}>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(lead.created_at)}</td>
                      <td className="px-5 py-4">
                        <p className="font-bold text-navy">{lead.name}</p>
                        {lead.email && <p className="text-xs text-slate-500">{lead.email}</p>}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <a href={`tel:${lead.phone}`} className="font-bold text-amber-600 hover:underline">{lead.phone}</a>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{lead.service}</td>
                      <td className="px-5 py-4"><span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[11px] text-slate-600">{lead.source}</span></td>
                      <td className="max-w-[220px] px-5 py-4 text-slate-600">{lead.message || "—"}</td>
                      <td className="px-5 py-4">
                        <select
                          value={lead.status}
                          onChange={(e) => setStatus(lead.id, e.target.value)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-bold outline-none ${
                            lead.status === "new" ? "border-amber-300 bg-amber-50 text-amber-700" :
                            lead.status === "contacted" ? "border-blue-200 bg-blue-50 text-blue-700" :
                            lead.status === "won" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                            "border-slate-200 bg-slate-50 text-slate-500"
                          }`}
                          data-testid={`lead-status-select-${lead.id}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
